// src/pages/admin/ins-taller.tsx
// REFACTORIZADO: Usa el hook useTaller para centralizar la lógica

import React, { useEffect, useState, useRef } from "react";
import { collection, getDocs, query, where, addDoc, Timestamp } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import Swal from "sweetalert2";
import { QRCodeCanvas } from "qrcode.react";
import UserNav from "../../components/navbar/user-nav";
import FooterTop from "../../components/footer-top";
import Footer from "../../components/footer";
import { useParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { v4 as uuidv4 } from "uuid";

// Importar el hook y tipos desde useTaller
import { useTaller, Taller, UserPerfil } from "../../hooks/useTaller";

// Importar componentes de loading y PageTitle
import { LoadingMessage, ErrorState, EmptyState } from "../../components/loading-states";
import PageTitle from "../../components/page-title";

// Schema de validación para inscripción pública (sin cuenta)
const schemaPublico = yup.object({
  nombre: yup.string().required("El nombre es obligatorio"),
  correo: yup.string().email("Correo inválido").required("El correo es obligatorio"),
  telefono: yup.string().required("El teléfono es obligatorio"),
  inmobiliaria: yup.string().default(""),
});

export default function InscripcionPrivada() {
  const { tallerId } = useParams<{ tallerId: string }>();
  const [user, setUser] = useState<any>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [perfil, setPerfil] = useState<UserPerfil | null>(null);
  const [inscritos, setInscritos] = useState<{ [key: string]: boolean }>({});
  const [cupoLleno, setCupoLleno] = useState<{ [key: string]: boolean }>({});
  const [qrGenerado, setQrGenerado] = useState<{ [key: string]: string }>({});
  const qrRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const qrPublicoRef = useRef<HTMLDivElement | null>(null);

  // Estado para formulario público (usuarios sin cuenta)
  const [qrPublico, setQrPublico] = useState<string | null>(null);
  const [guardandoPublico, setGuardandoPublico] = useState(false);
  const {
    register: registerPublico,
    handleSubmit: handleSubmitPublico,
    formState: { errors: erroresPublico },
    reset: resetPublico,
  } = useForm({ resolver: yupResolver(schemaPublico) });

  // ========== USAR EL HOOK useTaller ==========
  const {
    talleres,
    taller: tallerEspecifico,
    loading,
    error,
    fetchAllTalleres,
    fetchTallerById,
    puedeInscribirse,
    verificarCupoDisponible,
    crearInscripcion,
    formatearCosto
  } = useTaller();

  // Detectar usuario autenticado
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Detectar perfil del usuario (afiliado o asociado)
  useEffect(() => {
    const detectarPerfil = async () => {
      if (!user) return;

      const qAsesor = query(collection(db, "asesor"), where("correo", "==", user.email));
      const qAsociado = query(collection(db, "asociado"), where("correo", "==", user.email));
      const [snapAfi, snapAso] = await Promise.all([getDocs(qAsesor), getDocs(qAsociado)]);

      if (!snapAfi.empty) {
        setPerfil({ id: snapAfi.docs[0].id, tipo: "afiliado" });
      } else if (!snapAso.empty) {
        setPerfil({ id: snapAso.docs[0].id, tipo: "asociado" });
      }
    };
    detectarPerfil();
  }, [user]);

  // Cargar taller(es) cuando hay perfil detectado
  useEffect(() => {
    const cargarDatos = async () => {
      if (!perfil) return;

      if (tallerId) {
        // Cargar taller específico
        await fetchTallerById(tallerId);
      } else {
        // Cargar todos los talleres próximos
        await fetchAllTalleres(false);
      }
    };

    cargarDatos();
  }, [perfil, tallerId, fetchTallerById, fetchAllTalleres]);

  // Cargar taller(es) para modo público (sin autenticación)
  useEffect(() => {
    if (authLoading || user) return;
    if (tallerId) {
      fetchTallerById(tallerId);
    }
  }, [authLoading, user, tallerId, fetchTallerById]);

  // Verificar inscripciones y cupo cuando cambian los talleres
  useEffect(() => {
    const verificarEstados = async () => {
      if (!perfil) return;

      const talleresAVerificar = tallerId && tallerEspecifico ? [tallerEspecifico] : talleres;
      const newInsc: { [key: string]: boolean } = {};
      const newCupo: { [key: string]: boolean } = {};

      for (const taller of talleresAVerificar) {
        // Verificar si ya está inscrito
        const insSnap = await getDocs(
          query(
            collection(db, "inscripcion"),
            where("perfil_id", "==", perfil.id),
            where("taller_doc_id", "==", taller.id)
          )
        );
        if (!insSnap.empty) newInsc[taller.id as string] = true;

        // Verificar cupo
        const hayEspacio = await verificarCupoDisponible(
          taller.id as string,
          taller.cupoMaximo || (taller.cupo ? parseInt(taller.cupo) : undefined)
        );
        if (!hayEspacio) newCupo[taller.id as string] = true;
      }

      setInscritos(newInsc);
      setCupoLleno(newCupo);
    };

    if ((tallerId && tallerEspecifico) || (!tallerId && talleres.length > 0)) {
      verificarEstados();
    }
  }, [talleres, tallerEspecifico, tallerId, perfil, verificarCupoDisponible]);

  // Filtrar talleres próximos (solo para vista general)
  const talleresFiltrados = tallerId
    ? (tallerEspecifico ? [tallerEspecifico] : [])
    : talleres.filter((t) => {
        const hoy = new Date();
        const fechaEvento = t.fecha?.toDate?.() || t.fechaTermino?.toDate?.() || t.fechaInicio?.toDate?.();
        return fechaEvento && fechaEvento > hoy;
      });

  // Handler de inscripción usando el hook
  const handleInscripcion = async (taller: Taller) => {
    if (!perfil) return;

    const resultado = await crearInscripcion(
      taller.id as string,
      perfil.id,
      perfil.tipo,
      { creditos: taller.creditos || 0, tipoCosto: taller.tipoCosto }
    );

    if (resultado.success && resultado.codigoQR) {
      setQrGenerado((prev) => ({ ...prev, [taller.id as string]: resultado.codigoQR! }));
      setInscritos((prev) => ({ ...prev, [taller.id as string]: true }));
      Swal.fire("¡Inscripción exitosa!", "Se generó tu código QR.", "success");
    } else {
      Swal.fire("Error", resultado.error || "No se pudo registrar. Intenta más tarde.", "error");
    }
  };

  // Handler para descargar QR
  const handleDownloadQR = (tallerIdLocal: string, nombreTaller: string) => {
    const container = qrRefs.current[tallerIdLocal];
    if (!container) return;

    const canvas = container.querySelector("canvas");
    if (!canvas) return;

    const pngUrl = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = pngUrl;
    link.download = `QR-${nombreTaller}.png`;
    link.click();
  };

  // Handler de inscripción para público en general (sin cuenta)
  const handleInscripcionPublica = async (data: any) => {
    if (!tallerId) return;
    setGuardandoPublico(true);
    try {
      // Verificar duplicado por correo + taller
      const q = query(
        collection(db, "externo_inscripcion"),
        where("correo", "==", data.correo),
        where("taller_doc_id", "==", tallerId)
      );
      const dupSnap = await getDocs(q);
      if (!dupSnap.empty) {
        Swal.fire({
          icon: "warning",
          title: "Ya estás registrado",
          text: "Este correo ya fue inscrito en este taller.",
        });
        setGuardandoPublico(false);
        return;
      }

      // Verificar cupo disponible
      const tallerData = tallerEspecifico || talleres.find((t) => t.id === tallerId);
      if (tallerData) {
        const cupo = tallerData.cupoMaximo || (tallerData.cupo ? parseInt(tallerData.cupo) : undefined);
        const hayEspacio = await verificarCupoDisponible(tallerId, cupo);
        if (!hayEspacio) {
          Swal.fire({
            icon: "error",
            title: "Cupo lleno",
            text: "Ya no hay lugares disponibles en este taller.",
          });
          setGuardandoPublico(false);
          return;
        }
      }

      const codigoQR = uuidv4();
      await addDoc(collection(db, "externo_inscripcion"), {
        taller_doc_id: tallerId,
        nombre: data.nombre,
        correo: data.correo,
        telefono: data.telefono || "",
        inmobiliaria: data.inmobiliaria || "",
        codigo_qr: codigoQR,
        fechaInscripcion: Timestamp.now(),
        asistencia: false,
        completado: false,
        estatus_pago: "pendiente",
      });

      setQrPublico(codigoQR);
      resetPublico();
      Swal.fire({
        icon: "success",
        title: "¡Registro exitoso!",
        text: "Tu inscripción fue registrada. Guarda tu código QR.",
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo registrar. Intenta más tarde.",
      });
    } finally {
      setGuardandoPublico(false);
    }
  };

  // Handler para descargar QR público
  const handleDownloadQRPublico = (nombreTaller: string) => {
    if (!qrPublicoRef.current) return;
    const canvas = qrPublicoRef.current.querySelector("canvas");
    if (!canvas) return;
    const pngUrl = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = pngUrl;
    link.download = `QR-${nombreTaller}.png`;
    link.click();
  };

  // Componente para mostrar estado de inscripción
  const mensajeDeEstado = (taller: Taller) => {
    if (cupoLleno[taller.id as string]) {
      return <div className="alert alert-danger mb-0">Cupo lleno</div>;
    }
    if (inscritos[taller.id as string]) {
      return <div className="alert alert-info mb-0">Ya estás inscrito</div>;
    }
    if (!puedeInscribirse(taller, perfil || undefined)) {
      return <div className="alert alert-warning mb-0">Tu perfil no tiene permisos para este taller</div>;
    }
    return (
      <button className="btn btn-primary" onClick={() => handleInscripcion(taller)}>
        Inscribirme
      </button>
    );
  };

  // ========== RENDER ==========

  // Estado de carga de autenticación
  if (authLoading) {
    return (
      <>
        <UserNav />
        <PageTitle
          title={tallerId ? "Inscripción al Taller" : "Inscripción de Usuarios Registrados"}
          subtitle="Verifica tu sesión"
        />
        <section className="gray-simple">
          <div className="container py-5">
            <LoadingMessage message="Verificando sesión..." />
          </div>
        </section>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  // Usuario no autenticado — Formulario de inscripción pública
  if (!user) {
    const tallerPublico = tallerEspecifico || talleres.find((t) => t.id === tallerId);

    // Si no hay tallerId, mostrar enlace a catálogo de talleres
    if (!tallerId) {
      return (
        <>
          <UserNav />
          <PageTitle title="Inscripción a Talleres" subtitle="Selecciona un taller" />
          <section className="gray-simple">
            <div className="container py-5">
              <div className="alert alert-info text-center">
                <i className="fa-solid fa-calendar me-2" aria-hidden="true"></i>
                Visita nuestro catálogo de talleres para seleccionar uno.
              </div>
              <div className="text-center mt-3">
                <Link to="/ver-taller" className="btn btn-primary">
                  <i className="fa-solid fa-calendar-check me-2" aria-hidden="true"></i>
                  Ver Talleres Disponibles
                </Link>
              </div>
              <div className="text-center mt-3">
                <span className="text-muted">¿Ya tienes cuenta?</span>{" "}
                <Link to="/login">Inicia sesión aquí</Link>
              </div>
            </div>
          </section>
          <FooterTop bg="theme-bg" />
          <Footer />
        </>
      );
    }

    // Loading del taller
    if (loading) {
      return (
        <>
          <UserNav />
          <PageTitle title="Inscripción al Taller" subtitle="Cargando información" />
          <section className="gray-simple">
            <div className="container py-5">
              <LoadingMessage message="Cargando información del taller..." />
            </div>
          </section>
          <FooterTop bg="theme-bg" />
          <Footer />
        </>
      );
    }

    // Taller no encontrado
    if (!tallerPublico) {
      return (
        <>
          <UserNav />
          <PageTitle title="Inscripción al Taller" subtitle="Taller no encontrado" />
          <section className="gray-simple">
            <div className="container py-5">
              <EmptyState
                icon="fa-solid fa-circle-exclamation"
                title="Taller no encontrado"
                message="El taller solicitado no existe o no está disponible."
                actionText="Ver todos los talleres"
                onAction={() => (window.location.href = "/ver-taller")}
              />
            </div>
          </section>
          <FooterTop bg="theme-bg" />
          <Footer />
        </>
      );
    }

    const fechaPublico = tallerPublico.fecha?.toDate?.() || tallerPublico.fechaInicio?.toDate?.();

    return (
      <>
        <UserNav />
        <PageTitle title="Inscripción al Taller" subtitle="Registro para público en general" />
        <section className="gray-simple">
          <div className="container py-5">
            {/* Info del taller */}
            <div className="card mb-4 p-4 border rounded shadow-sm">
              <h4 className="mb-3">{tallerPublico.nombre}</h4>
              <div className="row">
                <div className="col-md-6">
                  <p className="mb-1"><strong>Fecha:</strong> {fechaPublico?.toLocaleDateString("es-MX") || "Por definir"}</p>
                  <p className="mb-1">
                    <strong>Horario:</strong>{" "}
                    {tallerPublico.horaInicio && tallerPublico.horaTermino
                      ? `${tallerPublico.horaInicio} - ${tallerPublico.horaTermino}`
                      : tallerPublico.horario || "Por definir"}
                  </p>
                  <p className="mb-1"><strong>Lugar:</strong> {tallerPublico.lugar}</p>
                </div>
                <div className="col-md-6">
                  <p className="mb-1"><strong>Organizador:</strong> {tallerPublico.organizadorPrincipal || tallerPublico.expositor || "AMPI"}</p>
                  <p className="mb-1"><strong>Costo:</strong> {formatearCosto(tallerPublico)}</p>
                  {tallerPublico.tipoEvento && (
                    <p className="mb-1"><strong>Modalidad:</strong> <span className="badge bg-info">{tallerPublico.tipoEvento}</span></p>
                  )}
                </div>
              </div>
            </div>

            {/* Aviso de login para afiliados/asociados */}
            <div className="alert alert-info text-center">
              <i className="fa-solid fa-user-check me-2" aria-hidden="true"></i>
              ¿Eres afiliado o asociado AMPI?{" "}
              <Link to="/login">Inicia sesión aquí</Link> para inscribirte con tu cuenta y acumular créditos.
            </div>

            {/* QR generado exitosamente */}
            {qrPublico ? (
              <div className="card p-4 text-center shadow-sm">
                <h5 className="text-success mb-3">
                  <i className="fa-solid fa-circle-check me-2" aria-hidden="true"></i>
                  ¡Inscripción exitosa!
                </h5>
                <p>Guarda tu código QR para presentarlo el día del evento.</p>
                <div ref={qrPublicoRef} className="d-inline-block">
                  <QRCodeCanvas value={qrPublico} size={200} />
                </div>
                <div className="mt-3">
                  <button
                    onClick={() => handleDownloadQRPublico(tallerPublico.nombre)}
                    className="btn btn-outline-primary"
                  >
                    <i className="fa-solid fa-download me-2" aria-hidden="true"></i>
                    Descargar QR
                  </button>
                </div>
                <div className="mt-3">
                  <Link to="/ver-taller" className="btn btn-outline-secondary">
                    <i className="fa-solid fa-arrow-left me-2" aria-hidden="true"></i>
                    Ver más talleres
                  </Link>
                </div>
              </div>
            ) : (
              /* Formulario de inscripción pública */
              <div className="card p-4 shadow-sm">
                <h5 className="mb-3">
                  <i className="fa-solid fa-clipboard-list me-2" aria-hidden="true"></i>
                  Formulario de Inscripción
                </h5>
                <form onSubmit={handleSubmitPublico(handleInscripcionPublica)}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Nombre completo <span className="text-danger">*</span></label>
                      <input className="form-control" placeholder="Tu nombre completo" {...registerPublico("nombre")} />
                      {erroresPublico.nombre && <small className="text-danger">{erroresPublico.nombre.message}</small>}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Correo electrónico <span className="text-danger">*</span></label>
                      <input className="form-control" type="email" placeholder="correo@ejemplo.com" {...registerPublico("correo")} />
                      {erroresPublico.correo && <small className="text-danger">{erroresPublico.correo.message}</small>}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Teléfono <span className="text-danger">*</span></label>
                      <input className="form-control" placeholder="+52 322 123 4567" {...registerPublico("telefono")} />
                      {erroresPublico.telefono && <small className="text-danger">{erroresPublico.telefono.message}</small>}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Inmobiliaria / Empresa</label>
                      <input className="form-control" placeholder="Nombre de tu inmobiliaria (opcional)" {...registerPublico("inmobiliaria")} />
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <button type="submit" className="btn btn-primary btn-lg" disabled={guardandoPublico}>
                      {guardandoPublico ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Registrando...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-check me-2" aria-hidden="true"></i>
                          Inscribirme
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </section>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  // Estado de carga de talleres
  if (loading) {
    return (
      <>
        <UserNav />
        <PageTitle
          title={tallerId ? "Inscripción al Taller" : "Inscripción de Usuarios Registrados"}
          subtitle="Cargando información"
        />
        <section className="gray-simple">
          <div className="container py-5">
            <LoadingMessage message="Cargando talleres..." subMessage="Por favor espera" />
          </div>
        </section>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  // Estado de error
  if (error) {
    return (
      <>
        <UserNav />
        <PageTitle
          title={tallerId ? "Inscripción al Taller" : "Inscripción de Usuarios Registrados"}
          subtitle="Error"
        />
        <section className="gray-simple">
          <div className="container py-5">
            <ErrorState
              title="Error al cargar talleres"
              message={error}
              onRetry={() => (tallerId ? fetchTallerById(tallerId) : fetchAllTalleres(false))}
            />
          </div>
        </section>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  // Taller específico no encontrado
  if (tallerId && talleresFiltrados.length === 0) {
    return (
      <>
        <UserNav />
        <PageTitle title="Inscripción al Taller" subtitle="Taller no encontrado" />
        <section className="gray-simple">
          <div className="container py-5">
            <EmptyState
              icon="fa-solid fa-circle-exclamation"
              title="Taller no encontrado"
              message="El taller solicitado no existe o no está disponible."
              actionText="Ver todos los talleres"
              onAction={() => (window.location.href = "/ver-talleres")}
            />
          </div>
        </section>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  // Sin talleres disponibles (vista general)
  if (!tallerId && talleresFiltrados.length === 0) {
    return (
      <>
        <UserNav />
        <PageTitle title="Inscripción de Usuarios Registrados" subtitle="Talleres disponibles" />
        <section className="gray-simple">
          <div className="container py-5">
            <EmptyState
              icon="fa-solid fa-calendar-xmark"
              title="Sin talleres disponibles"
              message="No hay talleres próximos disponibles para inscripción."
              actionText="Ver historial de talleres"
              onAction={() => (window.location.href = "/ver-talleres")}
            />
          </div>
        </section>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  return (
    <>
      <UserNav />
      <PageTitle
        title={tallerId ? "Inscripción al Taller" : "Inscripción de Usuarios Registrados"}
        subtitle={tallerId ? "Completa tu inscripción" : "Selecciona un taller para inscribirte"}
      />

      <section className="gray-simple">
        <div className="container">
          {talleresFiltrados.map((taller) => {
            const fecha = taller.fecha?.toDate?.() || taller.fechaInicio?.toDate?.();

            return (
              <div className="card mb-4 p-3 border rounded shadow-sm" key={taller.id}>
                <div className="row">
                  <div className="col-md-8">
                    <h5 className="mb-2">{taller.nombre}</h5>
                    <p className="mb-1">
                      <strong>Fecha:</strong>{" "}
                      {fecha?.toLocaleDateString("es-MX") || "Por definir"}
                    </p>
                    <p className="mb-1">
                      <strong>Horario:</strong>{" "}
                      {taller.horaInicio && taller.horaTermino
                        ? `${taller.horaInicio} - ${taller.horaTermino}`
                        : taller.horario || "Por definir"}
                    </p>
                    <p className="mb-1">
                      <strong>Organizador:</strong>{" "}
                      {taller.organizadorPrincipal || taller.expositor || "AMPI"}
                    </p>
                    <p className="mb-1">
                      <strong>Lugar:</strong> {taller.lugar}
                    </p>
                    {taller.ciudad && (
                      <p className="mb-1">
                        <strong>Ciudad:</strong> {taller.ciudad}
                      </p>
                    )}
                    {taller.tipoEvento && (
                      <p className="mb-1">
                        <strong>Modalidad:</strong>{" "}
                        <span className="badge bg-info">{taller.tipoEvento}</span>
                      </p>
                    )}
                    <p className="mb-1">
                      <strong>Costo:</strong> {formatearCosto(taller)}
                    </p>
                    <p className="mb-1">
                      <strong>Créditos:</strong> {taller.creditos || 0}
                    </p>
                    {taller.publicoObjetivo && (
                      <p className="mb-1">
                        <strong>Dirigido a:</strong> {taller.publicoObjetivo}
                      </p>
                    )}
                  </div>
                  <div className="col-md-4 d-flex flex-column justify-content-center align-items-center">
                    {mensajeDeEstado(taller)}
                    {qrGenerado[taller.id as string] && (
                      <div className="text-center mt-3">
                        <h5>Tu Código QR:</h5>
                        <div ref={(el) => (qrRefs.current[taller.id as string] = el)}>
                          <QRCodeCanvas value={qrGenerado[taller.id as string]} size={200} />
                        </div>
                        <div className="mt-2">
                          <button
                            onClick={() => handleDownloadQR(taller.id as string, taller.nombre)}
                            className="btn btn-sm btn-outline-primary"
                            aria-label={`Descargar código QR para ${taller.nombre}`}
                          >
                            <i className="fa-solid fa-download me-1" aria-hidden="true"></i>
                            Descargar QR
                          </button>
                          <p className="small text-muted mt-1">
                            Guárdalo para mostrarlo el día del evento.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Link de navegación */}
          {tallerId && (
            <div className="text-center mt-4">
              <Link to="/ver-talleres" className="btn btn-outline-secondary">
                <i className="fa-solid fa-arrow-left me-2" aria-hidden="true"></i>
                Ver todos los talleres
              </Link>
            </div>
          )}
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
